// York-fill prompt — "How full is your york?", shown after a Large
// order completes and BEFORE the scan-location prompt that opens the
// next one.
//
// Why it exists: a Large order is picked onto a york, and today the
// york goes back to the drop zone when the ORDER is done rather than
// when the york is full. Knowing the space left lets the allocator hand
// the picker an order that actually fits, so one trip can carry several
// orders. The scan-location step is unchanged and still follows this —
// it is what tells us where the picker is standing.
//
// NOT shown on the first Large order of a session: the picker has just
// fetched a york, so the answer is always "empty" and asking would be a
// tap for nothing. ActiveWaveScreen owns that decision (this component
// is only rendered when the question is worth asking).
//
// Tapping 4/4 never starts a wave — the caller routes straight to the
// drop-off screen. Every other level starts one, and the server may
// still come back YORK_FULL when nothing left in the queue fits, which
// lands on the same drop-off screen with a different reason.
import { useState } from "react";
import { ChevronRight, Check } from "lucide-react";
import YorkGlyph from "../picking-shared/YorkGlyph";
import { useTranslation } from "../../i18n";
import "./YorkFillPrompt.css";

export type YorkQuarters = 0 | 1 | 2 | 3 | 4;

interface Props {
    /** Resolves when the caller has acted on the answer. */
    onAnswer: (quarters: YorkQuarters) => void | Promise<void>;
}

const OPTIONS: { quarters: YorkQuarters; key: string }[] = [
    { quarters: 0, key: "picking_york_fill_empty" },
    { quarters: 1, key: "picking_york_fill_quarter" },
    { quarters: 2, key: "picking_york_fill_half" },
    { quarters: 3, key: "picking_york_fill_three_quarters" },
    { quarters: 4, key: "picking_york_fill_full" },
];

const YorkFillPrompt = ({ onAnswer }: Props) => {
    const { t } = useTranslation();
    // One answer per prompt. Without this a double-tap on a laggy
    // handheld starts two waves — the same class of bug the anchor
    // scan's `busy` guard exists for.
    const [busy, setBusy] = useState(false);

    const answer = async (quarters: YorkQuarters) => {
        if (busy) return;
        setBusy(true);
        try {
            await onAnswer(quarters);
        } finally {
            // The caller unmounts us on success (navigation / phase
            // change); re-enabling matters only when it does not, e.g. a
            // network failure the picker should be able to retry.
            setBusy(false);
        }
    };

    return (
        <div className="yfp">
            <div className="yfp-head">
                <div className="yfp-eyebrow">
                    <Check size={17} strokeWidth={2.6} aria-hidden="true" />
                    {t("picking_york_order_complete")}
                </div>
                <h1 className="yfp-title">{t("picking_york_fill_question")}</h1>
            </div>

            <div className="yfp-options" role="group" aria-label={t("picking_york_fill_question")}>
                {OPTIONS.map(({ quarters, key }) => {
                    const isFull = quarters === 4;
                    return (
                        <button
                            key={quarters}
                            type="button"
                            disabled={busy}
                            className={"yfp-option" + (isFull ? " yfp-option--full" : "")}
                            onClick={() => { void answer(quarters); }}
                        >
                            <YorkGlyph
                                quarters={quarters}
                                tone={isFull ? "#b84700" : "#5b6470"}
                            />
                            <span className="yfp-option-label">{t(key)}</span>
                            {isFull && (
                                <ChevronRight
                                    size={20}
                                    strokeWidth={2.4}
                                    className="yfp-option-chevron"
                                    aria-hidden="true"
                                />
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default YorkFillPrompt;
